import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Text,
    Group,
    Box,
    Loader,
    Code,
    Badge,
    Collapse,
    Stack,
    Image,
    Anchor,
} from '@mantine/core';
import {
    IconCircleCheck,
    IconChartBar,
    IconExternalLink,
    IconNote,
} from '@tabler/icons-react';
import TodoCreationResult from './TodoCreationResult';
import SearchNoteResult from './SearchNoteResult';
import GetTodoResult from './GetTodoResult';
import { getDateLocale } from '@/common/utils/i18nUtils';

// 노트 검색 결과 타입
interface NoteResult {
    id?: string;
    title?: string;
    date?: string;
    url?: string;
    author_name?: string; // 작성자 정보 추가
}

interface ToolUseProps {
    toolName: string;
    toolInput?: Record<string, unknown>;
    isCompleted?: boolean;
}

// 시각화 결과 인터페이스 추가
interface VisualizationResult {
    html_base64?: string;
    png_base64?: string;
    filename?: string;
    note?: string;
}

// 파싱된 콘텐츠 인터페이스 추가
interface ParsedContent {
    success?: boolean;
    status?: string;
    results?: unknown[];
    data?: unknown[];
    error?: unknown;
    rowcount?: number;
}

// 공통으로 사용할 스타일 상수 추가
const TOOL_BOX_STYLE = {
    width: '445px',
    maxWidth: '445px',
    marginBottom: '8px',
    backgroundColor: '#FFFFFF',
};

// 브랜드 컨셉에 맞는 색상 정의
const BRAND_COLORS = {
    primary: '#7a2d9e', // 진한 보라색
    secondary: '#5c6ec8', // 연한 파랑색
    accent: '#4a4f8a', // 중간 색상
    completed: '#228BE6', // 완료 상태 색상
    running: '#228BE6', // 실행 중 색상
    background: '#f9f5fd', // 연한 보라색 배경
    lightAccent: '#e3daf9', // 더 연한 보라색 액센트
};

const ToolNameDisplay: React.FC<{ name: string }> = ({ name }) => {
    // 도구 이름을 가독성 좋게 변환 (snake_case -> 공백 분리)
    const formattedName = name
        .replace(/_/g, ' ')
        .split(' ')
        .map((word, index) => {
            // 첫 번째 단어만 첫 글자를 대문자로 변환
            if (index === 0) {
                return (
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                );
            }
            // 나머지 단어는 소문자로 유지
            return word.toLowerCase();
        })
        .join(' ');

    return (
        <Text
            style={{
                fontFamily: 'Pretendard',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0%',
                color: '#343A40',
            }}
        >
            {formattedName}
        </Text>
    );
};

/**
 * 도구 사용을 표시하는 컴포넌트
 */
const ToolUseBox: React.FC<ToolUseProps> = ({
    toolName,
    toolInput = {},
    isCompleted = false,
}) => {
    const { t } = useTranslation('ai');
    const [expanded, setExpanded] = useState(false);
    const [animationPhase, setAnimationPhase] = useState(0);
    const [renderError, setRenderError] = useState<string | null>(null);
    const [renderMode, setRenderMode] = useState<'iframe' | 'direct'>('iframe');
    const [contentDecoded, setContentDecoded] = useState<boolean>(false);

    // 애니메이션 효과 (실행 중일 때만)
    useEffect(() => {
        if (isCompleted) return;

        const interval = setInterval(() => {
            setAnimationPhase(prev => (prev + 1) % 3);
        }, 800);

        return () => clearInterval(interval);
    }, [isCompleted]);

    // 결과가 있는 경우 자동으로 확장
    useEffect(() => {
        try {
            // 1. 일반적인 검색 조건 확인
            const hasStandardResults =
                (toolName === 'search_note' &&
                    toolInput.results &&
                    Array.isArray(toolInput.results) &&
                    toolInput.results.length > 0) ||
                (toolName === 'data_for_visualization' &&
                    (toolInput.html_base64 || toolInput.png_base64));

            // 2. 할 일 생성 결과 확인 (직접 속성 또는 JSON 문자열 내부)
            let hasTodoResult = false;

            if (toolName === 'create_todo') {
                console.log(
                    '[ToolUseBox] 할 일 생성 도구 감지, 입력 검사:',
                    toolInput
                );

                // 2a. 직접 todo_id 속성 체크
                if (
                    toolInput.todo_id &&
                    typeof toolInput.todo_id === 'string'
                ) {
                    hasTodoResult = true;
                }
                // 2b. data 배열 내부에 todo_id가 있는지 체크
                else if (
                    toolInput.data &&
                    Array.isArray(toolInput.data) &&
                    toolInput.data.length > 0
                ) {
                    const dataItem = toolInput.data[0] as Record<
                        string,
                        unknown
                    >;
                    if (
                        dataItem.todo_id &&
                        typeof dataItem.todo_id === 'string'
                    ) {
                        hasTodoResult = true;
                        // toolInput에 직접 todo_id 추가
                        toolInput.todo_id = dataItem.todo_id;
                    }
                }
                // 2c. 문자열로 된 JSON 객체 파싱 시도 (content 속성 체크)
                else if (
                    toolInput.content &&
                    typeof toolInput.content === 'string'
                ) {
                    try {
                        const parsedContent = JSON.parse(toolInput.content) as {
                            success?: boolean;
                            status?: string;
                            results?: unknown[];
                            data?: unknown[];
                            error?: unknown;
                            rowcount?: number;
                        };
                        console.log(
                            '[ToolUseBox] 문자열 콘텐츠 파싱 결과:',
                            parsedContent
                        );

                        // 파싱된 데이터에서 todo_id 찾기
                        if (
                            parsedContent.data &&
                            Array.isArray(parsedContent.data) &&
                            parsedContent.data.length > 0
                        ) {
                            const dataItem = parsedContent.data[0] as Record<
                                string,
                                unknown
                            >;
                            if (
                                dataItem.todo_id &&
                                typeof dataItem.todo_id === 'string'
                            ) {
                                hasTodoResult = true;
                                // toolInput에 직접 todo_id 추가
                                toolInput.todo_id = dataItem.todo_id;
                                // 다른 속성들도 추가
                                Object.assign(toolInput, parsedContent);
                            }
                        }
                    } catch (parseErr) {
                        console.warn(
                            '[ToolUseBox] 콘텐츠 파싱 실패:',
                            parseErr
                        );
                    }
                }
            }

            // 확장 조건을 충족하면 확장
            if (hasStandardResults || hasTodoResult) {
                setExpanded(true);
                console.log('[ToolUseBox] 결과가 있는 경우 자동으로 확장');

                // 시각화 도구 결과 디버깅 로그
                if (toolName === 'data_for_visualization') {
                    console.log('[ToolUseBox] 시각화 도구 결과 수신:', {
                        html_base64_length: toolInput.html_base64
                            ? typeof toolInput.html_base64 === 'string'
                                ? toolInput.html_base64.length
                                : '객체'
                            : 0,
                        png_base64_exists: !!toolInput.png_base64,
                        filename: toolInput.filename,
                    });
                }

                // 할 일 생성 결과 디버깅 로그
                if (toolName === 'create_todo') {
                    console.log('[ToolUseBox] 할 일 생성 결과 수신:', {
                        todo_id: toolInput.todo_id || 'N/A',
                        success: toolInput.success,
                        status: toolInput.status,
                        parsedFromContent: hasTodoResult && !toolInput.todo_id,
                    });
                }
            }
        } catch (error) {
            console.error('[ToolUseBox] 결과 확인 중 오류:', error);
        }
    }, [toolName, toolInput]);

    // 노트 검색 결과가 있는지 확인
    const hasNoteResults =
        toolName === 'search_note' &&
        toolInput.results &&
        Array.isArray(toolInput.results) &&
        toolInput.results.length > 0;
    // console.log('[ToolUseBox] 노트 검색 결과:', hasNoteResults);

    // get_todo_at_time 도구 확인 로그 추가
    // console.log(
    //     '[ToolUseBox] 도구 이름 확인:',
    //     toolName,
    //     toolName === 'get_todo_at_time'
    // );
    if (toolName === 'get_todo_at_time') {
        console.log(
            '[ToolUseBox] get_todo_at_time 도구 발견! 입력값:',
            toolInput
        );
    }

    // 시각화 결과가 있는지 확인
    const hasVisualizationResults =
        toolName === 'data_for_visualization' &&
        (toolInput.html_base64 ||
            (toolInput.png_base64 && toolInput.png_base64 !== ''));

    // 할 일 생성 결과가 있는지 확인
    const hasTodoCreationResult = (() => {
        // 1. 기본 조건 검사 - create_todo 도구인 경우 추가 검증 필요
        if (toolName === 'create_todo') {
            // todo_id가 있는지 확인
            if (toolInput.todo_id && typeof toolInput.todo_id === 'string') {
                return true;
            }
            // 그 외에는 아래 조건들로 계속 검사
        }

        // 2. unknown_json 도구이면서 todo_id가 있는지 확인
        if (
            toolName === 'unknown_json' &&
            typeof toolInput?.todo_id === 'string'
        ) {
            console.log('[ToolUseBox] unknown_json이지만 todo_id 패턴 감지');
            return true;
        }

        // 3. data 배열 내부에 todo_id가 있는지 확인
        if (
            toolInput.data &&
            Array.isArray(toolInput.data) &&
            toolInput.data.length > 0
        ) {
            const dataItem = toolInput.data[0] as Record<string, unknown>;
            if (dataItem.todo_id && typeof dataItem.todo_id === 'string') {
                // toolInput에 직접 todo_id 추가
                toolInput.todo_id = dataItem.todo_id;
                return true;
            }
        }

        // 4. 직접적인 todo_id 속성 확인
        if (toolInput.todo_id && typeof toolInput.todo_id === 'string') {
            return true;
        }

        // 5. 문자열 JSON 파싱 시도
        if (toolInput.content && typeof toolInput.content === 'string') {
            try {
                const parsedContent = JSON.parse(toolInput.content) as {
                    data?: unknown[];
                };

                // 파싱된 데이터에서 todo_id 찾기
                if (
                    parsedContent.data &&
                    Array.isArray(parsedContent.data) &&
                    parsedContent.data.length > 0
                ) {
                    const dataItem = parsedContent.data[0] as Record<
                        string,
                        unknown
                    >;
                    if (
                        dataItem.todo_id &&
                        typeof dataItem.todo_id === 'string'
                    ) {
                        // toolInput에 직접 todo_id 추가
                        toolInput.todo_id = dataItem.todo_id;
                        return true;
                    }
                }
                return false;
            } catch (e) {
                return false;
            }
        }

        return false;
    })();

    // 시각화 결과 타입으로 변환
    const visualizationData: VisualizationResult = {
        html_base64: toolInput.html_base64 as string,
        png_base64: toolInput.png_base64 as string,
        filename: toolInput.filename as string,
        note: toolInput.note as string,
    };

    // base64 디코딩 함수 (로그 추가)
    const decodeBase64 = (base64: string) => {
        try {
            console.log(
                '[ToolUseBox] Base64 디코딩 시도, 길이:',
                base64.length
            );
            if (!base64 || base64.trim() === '') {
                return '<div style="color: #888; text-align: center; padding: 20px;">비어있는 시각화 데이터</div>';
            }
            const decoded = atob(base64);
            console.log(
                '[ToolUseBox] Base64 디코딩 완료, 결과 길이:',
                decoded.length
            );

            // 디코딩된 내용 분석 (첫 100자만)
            const preview = decoded
                .substring(0, 100)
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            console.log(
                '[ToolUseBox] 디코딩된 내용 미리보기:',
                preview + '...'
            );

            // HTML이 유효한지 간단한 체크
            if (
                !decoded.includes('<html') &&
                !decoded.includes('<svg') &&
                !decoded.includes('<div')
            ) {
                console.warn(
                    '[ToolUseBox] 디코딩된 내용에 HTML/SVG 태그가 없습니다'
                );
            }

            setContentDecoded(true);
            return cleanHtml(decoded);
        } catch (err) {
            console.error('[ToolUseBox] Base64 디코딩 오류:', err);
            setRenderError(
                `디코딩 오류: ${err instanceof Error ? err.message : '알 수 없는 오류'}`
            );
            return '<div style="color: #d63031; text-align: center; padding: 20px;">시각화 데이터 디코딩 중 오류가 발생했습니다.</div>';
        }
    };

    // HTML 내용 정리 및 개선 함수
    const cleanHtml = (html: string): string => {
        try {
            // 기본 스타일 주입 (시각화가 제대로 표시되도록)
            if (!html.includes('<style')) {
                html = `<style>
                    body, html { margin: 0; padding: 0; width: 100%; height: 100%; overflow: hidden; }
                    svg, canvas { max-width: 100%; height: auto; display: block; margin: 0 auto; }
                    .plot-container { width: 100%; height: 100%; }
                </style>${html}`;
            }

            // HTML 문서가 아닌 경우 HTML 문서로 감싸기
            if (!html.includes('<html') && !html.includes('<!DOCTYPE')) {
                html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${html}</body></html>`;
            }

            return html;
        } catch (e) {
            console.error('[ToolUseBox] HTML 정리 중 오류:', e);
            return html; // 오류 발생 시 원본 반환
        }
    };

    // 렌더링 모드 전환 함수
    const toggleRenderMode = () => {
        setRenderMode(prev => (prev === 'iframe' ? 'direct' : 'iframe'));
        setRenderError(null); // 모드 전환 시 오류 상태 초기화
    };

    return (
        <Paper
            radius="md"
            shadow="none"
            bg="#FFFFFF"
            style={{
                ...TOOL_BOX_STYLE,
                width: '445px',
                transition: 'all 0.2s ease-in-out',
            }}
        >
            <Group justify="space-between">
                <Group>
                    <Box mr={4}>{/* {getToolIcon()} */}</Box>
                    <Box>
                        <Text size="sm" mb={6} fw={600}>
                            {isCompleted ? (
                                <Group gap={4}>
                                    <IconCircleCheck
                                        size={14}
                                        color={BRAND_COLORS.completed}
                                    />
                                    {t('tools.completed')}
                                </Group>
                            ) : (
                                <Group gap={4}>
                                    <Loader
                                        size="xs"
                                        color={BRAND_COLORS.running}
                                    />
                                    <Text>
                                        {t('tools.running')}
                                        {'.'.repeat(animationPhase + 1)}
                                        {' '.repeat(3 - animationPhase)}
                                    </Text>
                                </Group>
                            )}
                        </Text>
                        <Group gap={4} mb={10}>
                            <ToolNameDisplay name={toolName} />
                        </Group>
                    </Box>
                </Group>
                {(Object.keys(toolInput).length > 0 ||
                    hasNoteResults ||
                    hasVisualizationResults ||
                    hasTodoCreationResult) && (
                    <Box
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpanded(!expanded)}
                    >
                        {/* {expanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />} */}
                    </Box>
                )}
            </Group>

            <Collapse in={expanded}>
                {/* 노트 검색 결과 표시 */}
                {hasNoteResults && (
                    <SearchNoteResult
                        results={toolInput.results as NoteResult[]}
                    />
                )}

                {/* 시각화 결과 표시 */}
                {hasVisualizationResults && (
                    <Box
                        mt="xs"
                        p="xs"
                        style={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '4px',
                        }}
                    >
                        <Group
                            gap={6}
                            align="center"
                            mb="xs"
                            justify="space-between"
                        >
                            <Group gap={6}>
                                <IconChartBar size={14} />
                                <Text size="xs" fw={500}>
                                    {t('tools.visualizationResults')}
                                </Text>
                            </Group>
                            {visualizationData.html_base64 && (
                                <Badge
                                    size="xs"
                                    color={
                                        renderMode === 'iframe'
                                            ? 'blue'
                                            : 'grape'
                                    }
                                    style={{ cursor: 'pointer' }}
                                    onClick={toggleRenderMode}
                                >
                                    {renderMode === 'iframe'
                                        ? t('tools.iframeMode')
                                        : t('tools.directRendering')}
                                </Badge>
                            )}
                        </Group>

                        {visualizationData.png_base64 &&
                        visualizationData.png_base64 !== '' ? (
                            <Box mt="xs" style={{ textAlign: 'center' }}>
                                <Image
                                    src={`data:image/png;base64,${visualizationData.png_base64}`}
                                    alt="시각화 결과"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        margin: '0 auto',
                                    }}
                                    onError={e => {
                                        console.error(
                                            '[ToolUseBox] PNG 이미지 로드 오류:',
                                            e
                                        );
                                        setRenderError(
                                            'PNG 이미지를 로드할 수 없습니다.'
                                        );
                                    }}
                                />
                            </Box>
                        ) : visualizationData.html_base64 ? (
                            <Box mt="xs" style={{ textAlign: 'center' }}>
                                {(() => {
                                    console.log(
                                        '[ToolUseBox] HTML 렌더링 시도 - 모드:',
                                        renderMode
                                    );
                                    return null;
                                })()}

                                {renderMode === 'iframe' ? (
                                    <iframe
                                        srcDoc={decodeBase64(
                                            visualizationData.html_base64
                                        )}
                                        style={{
                                            width: '100%',
                                            height: '450px',
                                            border: '1px solid #eaeaea',
                                            borderRadius: '4px',
                                            backgroundColor: 'white',
                                        }}
                                        title="시각화 결과"
                                        sandbox="allow-scripts allow-same-origin allow-popups allow-downloads"
                                        onLoad={() => {
                                            console.log(
                                                '[ToolUseBox] iframe 로드 완료'
                                            );
                                            setRenderError(null);
                                        }}
                                        onError={e => {
                                            console.error(
                                                '[ToolUseBox] iframe 로드 오류:',
                                                e
                                            );
                                            setRenderError(
                                                'iframe을 로드할 수 없습니다.'
                                            );
                                        }}
                                    />
                                ) : (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: decodeBase64(
                                                visualizationData.html_base64
                                            ),
                                        }}
                                        style={{
                                            width: '100%',
                                            height: '450px',
                                            border: '1px solid #eaeaea',
                                            borderRadius: '4px',
                                            backgroundColor: 'white',
                                            overflow: 'auto',
                                        }}
                                    />
                                )}

                                {renderError && (
                                    <Badge
                                        size="sm"
                                        color="grape"
                                        variant="light"
                                        mr={4}
                                    >
                                        {renderError}
                                    </Badge>
                                )}

                                {!renderError && !contentDecoded && (
                                    <Text size="xs" color="dimmed" mt={8}>
                                        {t('tools.loadingVisualizationData')}
                                    </Text>
                                )}
                            </Box>
                        ) : (
                            <Text size="sm" color="dimmed" ta="center">
                                {t('tools.unableToDisplayVisualization')}
                            </Text>
                        )}

                        {visualizationData.filename && (
                            <Text size="xs" color="dimmed" mt={8} ta="center">
                                {t('tools.filename')}{' '}
                                {visualizationData.filename}
                            </Text>
                        )}

                        {visualizationData.note && (
                            <Text size="xs" color="dimmed" mt={4} ta="center">
                                {visualizationData.note}
                            </Text>
                        )}
                    </Box>
                )}

                {/* 일반 도구 파라미터 표시 */}
                {Object.keys(toolInput).length > 0 &&
                    !hasNoteResults &&
                    !hasVisualizationResults &&
                    !hasTodoCreationResult && (
                        <Box
                            mt="xs"
                            p="xs"
                            style={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '4px',
                            }}
                        >
                            <Text size="xs" fw={500} mb="xs">
                                {t('tools.inputParameters')}
                            </Text>
                            <Code
                                block
                                style={{
                                    background: 'rgba(122, 45, 158, 0.05)',
                                    fontSize: '12px',
                                }}
                            >
                                {JSON.stringify(toolInput, null, 2)}
                            </Code>
                        </Box>
                    )}
            </Collapse>
        </Paper>
    );
};

export default ToolUseBox;

/**
 * 실시간으로 사용 중인 도구 목록을 표시하는 컴포넌트
 */
interface ToolInfo {
    name: string;
    input?: Record<string, unknown>;
    isCompleted?: boolean;
    specialType?: string; // 특수 도구 유형 추가
}

interface ActiveToolsProps {
    tools: ToolInfo[];
}

export const ActiveToolsList: React.FC<ActiveToolsProps> = ({ tools }) => {
    if (tools.length === 0) return null;

    // 도구 이름 기준으로 중복 제거 (가장 최근 상태의 도구만 유지)
    const uniqueTools: Record<string, ToolInfo> = {};
    tools.forEach(tool => {
        // 같은 이름의 도구가 여러 개 있을 경우 마지막 것으로 덮어씀
        if (tool.name) {
            uniqueTools[tool.name] = tool;
        }
    });

    // 중복 제거된 도구 목록으로 변환
    const deduplicatedTools = Object.values(uniqueTools);

    // 할 일 생성 도구 찾기
    const todoCreationTool = deduplicatedTools.find(
        tool =>
            tool.name === 'create_todo' &&
            tool.isCompleted &&
            tool.input?.todo_id
    );

    // get_todo_at_time 도구 찾기
    const getTodoAtTimeTool = deduplicatedTools.find(
        tool =>
            tool.name === 'get_todo_at_time' &&
            tool.isCompleted &&
            tool.input?.todo_id
    );

    return (
        <Box style={{ marginBottom: '4px', width: '100%' }}>
            {/* 일반 도구 표시 */}
            {deduplicatedTools.map((tool, index) => (
                <ToolUseBox
                    key={`tool-${index}-${tool.name}`}
                    toolName={tool.name}
                    toolInput={tool.input}
                    isCompleted={tool.isCompleted}
                />
            ))}

            {/* 할 일 생성 결과를 별도로 표시 */}
            {typeof todoCreationTool?.input?.todo_id === 'string' && (
                <TodoCreationResult todoId={todoCreationTool.input.todo_id} />
            )}

            {/* get_todo_at_time 결과를 별도로 표시 */}
            {typeof getTodoAtTimeTool?.input?.todo_id === 'string' && (
                <GetTodoResult
                    todoId={getTodoAtTimeTool.input.todo_id}
                    plannerId={
                        (getTodoAtTimeTool.input.planner_id as string) || ''
                    }
                />
            )}
        </Box>
    );
};

// 서버에서 오는 도구 사용 메시지 인터페이스
interface ToolUseResponseContent {
    name?: string;
    type?: string;
    input?: Record<string, unknown>;
}

interface ToolUseResponse {
    node?: string;
    content?: ToolUseResponseContent[];
    register_id?: string;
    session_id?: string;
    type?: string;
}
